// 실시간 이벤트 카탈로그 — 이름표·등급·색·유지시간 (DSN-18 Part 2)
//
// **이 표가 화면 표기의 정본이다.** 유형을 더할 때 여기 한 줄만 넣으면 이름표·등급·색·
// 유지시간이 따라온다. 코드 목록의 정본은 Core 의 LiveEventType(OpenAPI 계약)이고,
// 여기는 그것을 사람이 읽는 것으로 옮기는 자리다.
//
// ⚠️ **표에 없는 코드는 감추지 않고 코드를 그대로 보여준다.** 감사 로그에서 이름표를
//    두 곳에 두었다가 원시 코드가 화면에 노출된 일이 있었는데, 그때 배운 것은
//    "표를 한 벌로 두라"와 "빠진 것을 감추지 말라" 둘이다. 감추면 무슨 일이
//    있었는지 잃는다.

/** 등급 — 4단계. '심각'은 유형이 아니라 **임계 초과로 승격**되는 자리다 */
export type LiveTier = "normal" | "notice" | "warn" | "critical";

export interface LiveEventSpec {
  readonly code: string;
  readonly label: string;
  readonly tier: LiveTier;
  /**
   * 이 유형이 임계를 넘으면 '심각'으로 올린다. null 이면 승격하지 않는다.
   *
   * 참고 등급은 기본적으로 승격하지 않는다 — 막힌 것이 **정상 동작**이라
   * 건수가 많은 것 자체는 사고가 아니다. 화면에서 켤 수 있다.
   */
  readonly criticalAt: number | null;
}

/**
 * ⚠️ 임계 기본값 20·10 은 **근거 없는 첫 추정**이다. 실사용을 관측한 뒤 다시 정한다.
 *    그래서 화면에서 조정할 수 있게 했다.
 */
export const LIVE_EVENTS: readonly LiveEventSpec[] = [
  // 정상 활동 — 브랜드 색조, 한 프레임만 남는다
  { code: "LINK_CLICK", label: "링크 클릭", tier: "normal", criticalAt: null },
  { code: "LOGIN", label: "로그인", tier: "normal", criticalAt: null },
  { code: "LOGOUT", label: "로그아웃", tier: "normal", criticalAt: null },
  { code: "SIGNUP", label: "가입", tier: "normal", criticalAt: null },
  { code: "WITHDRAW", label: "해지", tier: "normal", criticalAt: null },

  // 참고 — 막힌 것이 정상 동작인 경우. 오커, 30초
  { code: "BLOCKED_ACCESS", label: "차단된 링크 접근", tier: "notice", criticalAt: null },
  { code: "QUOTA_EXCEEDED", label: "클릭제한·쿼타 초과", tier: "notice", criticalAt: null },

  // 주의 — 사람이 봐야 하는 것. 러스트 윤곽선, 3분
  { code: "LOGIN_FAILED", label: "로그인 실패 · 잠김", tier: "warn", criticalAt: 20 },
  { code: "SERVER_ERROR", label: "서버 오류 (5xx)", tier: "warn", criticalAt: 10 },
] as const;

const BY_CODE = new Map(LIVE_EVENTS.map((e) => [e.code, e]));

export function liveEventSpec(code: string): LiveEventSpec {
  // 표에 없으면 코드를 이름표로 쓴다 — 감추지 않는다
  return BY_CODE.get(code) ?? { code, label: code, tier: "normal", criticalAt: null };
}

/** 등급별 기본 유지시간(초). 0 은 '한 프레임만', null 은 '사람이 지울 때까지' */
export const TIER_RETENTION: Record<LiveTier, number | null> = {
  normal: 0,
  notice: 30,
  warn: 180,
  critical: null,
};

export const TIER_LABEL: Record<LiveTier, string> = {
  normal: "정상",
  notice: "참고",
  warn: "주의",
  critical: "심각",
};

/**
 * 등급 순서 — 큰 쪽이 강하다. 한 국가에 여러 등급이 겹치면 **가장 강한 것으로 칠한다.**
 * 정상 1,000건과 심각 1건이 함께 있으면 심각이 보여야 한다.
 */
export const TIER_RANK: Record<LiveTier, number> = {
  normal: 0,
  notice: 1,
  warn: 2,
  critical: 3,
};

/** 화면에서 조정할 수 있는 것들의 기본값 */
export const LIVE_DEFAULTS = {
  /** 표시 생기(초). 5초로 시작해 체감 부하를 보고 조정하기로 정했다 */
  windowSeconds: 5,
  /** 전광판 모드에서 심각 표식이 스스로 사라지는 시간(초) — 지울 사람이 없기 때문이다 */
  kioskCriticalSeconds: 600,
  /** 무활동 자동 정지(초). 전광판 모드에서는 적용하지 않는다 */
  idlePauseSeconds: 600,
} as const;
